import { S25Util } from "../../util/s25-util";
import { BookType, WorkBook, WritingOptions } from "./types/sheet.js.types";
import { SheetEntry, Stox, XLSX } from "./types/sheet.js.types.expanded";

export class SpreadSheetService {
    static scriptsPromise: Promise<void> = null;
    static DEFAULT_BOOK_TYPE: BookType = "xlsx";

    static LIMITATIONS: Partial<
        Record<
            BookType,
            { sheetNameMaxLength?: number; sheetNameIllegalChars?: RegExp; allowDuplicateSheetNames?: boolean }
        >
    > = {
        // I was unable to find the documentation for these limitations, so this is just empirical
        xlsx: {
            sheetNameMaxLength: 31,
            sheetNameIllegalChars: /[\-:]/g,
            allowDuplicateSheetNames: false,
        },
    };

    public static load() {
        SpreadSheetService.scriptsPromise =
            SpreadSheetService.scriptsPromise ||
            S25Util.all([]).then(async () => {
                await S25Util.loadScript(
                    "xlsx-spreadsheet-css",
                    "https://unpkg.com/x-data-spreadsheet@1.1.9/dist/xspreadsheet.css",
                );
                await S25Util.loadScript(
                    "xlsx-spreadsheet-js",
                    "https://unpkg.com/x-data-spreadsheet@1.1.9/dist/xspreadsheet.js",
                );
                await S25Util.loadScript(
                    "xlsx-shim-js",
                    "https://cdn.sheetjs.com/xlsx-latest/package/dist/shim.min.js",
                );
                await S25Util.loadScript(
                    "xlsx-full-js",
                    "https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js",
                );
                await S25Util.loadScript(
                    "xlsx-xlsxspread-js",
                    "https://cdn.sheetjs.com/xspreadsheet/xlsxspread.min.js",
                );
                return;
            });
        return SpreadSheetService.scriptsPromise;
    }

    public static async XLSX(): Promise<XLSX> {
        await SpreadSheetService.load();
        return window.XLSX;
    }

    public static async stox(workbook: WorkBook): Promise<Stox> {
        await SpreadSheetService.load();
        return window.stox(workbook);
    }

    public static async toSpreadsheet(sheetData: SheetEntry[], sheetName: string = "Sheet1") {
        return this.toSpreadsheets([{ name: sheetName, data: sheetData }]);
    }

    public static async toSpreadsheets(sheets: Sheet[]) {
        const xlsx = await SpreadSheetService.XLSX();
        const worksheets = sheets.map((sheet) => ({
            name: sheet.name,
            data: xlsx.utils.json_to_sheet(sheet.data),
        }));
        const workbook = xlsx.utils.book_new();
        for (const sheet of worksheets) {
            xlsx.utils.book_append_sheet(workbook, sheet.data, sheet.name);
        }
        return workbook;
    }

    public static async downloadExcel(
        sheetData: SheetEntry[],
        filename: string,
        sheetName: string = "Sheet1",
        options?: WritingOptions,
    ) {
        return this.downloadExcelMulti([{ name: sheetName, data: sheetData }], filename, options);
    }

    public static async downloadExcelMulti(sheets: Sheet[], filename: string, options?: WritingOptions) {
        sheets = this.normalizeSheets(options?.bookType ?? this.DEFAULT_BOOK_TYPE, sheets);
        const wb = await SpreadSheetService.toSpreadsheets(sheets);
        const xlsx = await SpreadSheetService.XLSX();
        xlsx.writeFile(wb, filename, options);
    }

    public static rowsToCsv<TRow extends object>(rows: TRow[], columns: CsvColumn<TRow>[]): string {
        const headerLine = columns.map((column) => this.escapeCsvCell(column.label)).join(",");
        const dataLines = rows.map((row) =>
            columns.map((column) => this.escapeCsvCell(String(row[column.key] ?? ""))).join(","),
        );
        return [headerLine, ...dataLines].join("\r\n");
    }

    public static downloadCsvContent(csvContent: string, filename: string): void {
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(objectUrl);
    }

    public static downloadCsv<TRow extends object>(rows: TRow[], columns: CsvColumn<TRow>[], filename: string): void {
        this.downloadCsvContent(this.rowsToCsv(rows, columns), filename);
    }

    public static async readExcelFile<T>(file: Blob): Promise<T> {
        await SpreadSheetService.load();
        const reader = new FileReader();

        const { promise, resolve } = S25Util.createPromise<T>();

        reader.onload = function (event) {
            const data = event.target.result;
            const workbook = window.XLSX.read(data, { type: "binary" });
            const sheets = workbook.SheetNames.map((name: string) => {
                return window.XLSX.utils.sheet_to_row_object_array(workbook.Sheets[name]);
            });
            resolve(sheets);
        };

        reader.onerror = () => {
            alert("Something went wrong");
        };

        reader.readAsArrayBuffer(file);

        return promise;
    }

    public static normalizeSheets(type: BookType, sheets: Sheet[]): Sheet[] {
        // Normalize sheet names
        const names = new Set<string>();
        return sheets.map((sheet) => {
            const { sheetNameMaxLength, sheetNameIllegalChars, allowDuplicateSheetNames } = {
                sheetNameMaxLength: Infinity,
                ...(this.LIMITATIONS[type] ?? {}),
            };

            let name = sheet.name.trim();
            // Remove illegal characters from sheet names
            if (sheetNameIllegalChars) {
                name = name.replace(sheetNameIllegalChars, "");
            }
            // Truncate sheet names to fit within the allowed length
            if (name.length > sheetNameMaxLength) {
                name = name.slice(0, sheetNameMaxLength - 1) + "…";
            }
            // Add a number suffix to duplicate sheet names
            if (!allowDuplicateSheetNames) {
                if (names.has(name)) {
                    let newName = name;
                    let i = 1;
                    while (names.has(newName)) {
                        let tag = ` (${i})`;
                        if (name.length + tag.length + 1 > sheetNameMaxLength) tag = `…${tag}`;
                        newName = name.slice(0, sheetNameMaxLength - tag.length).trim() + tag;
                        i += 1;
                    }
                    name = newName;
                }
            }
            names.add(name);

            return { ...sheet, name };
        });
    }

    private static escapeCsvCell(value: string): string {
        if (value.includes('"') || value.includes(",") || value.includes("\n") || value.includes("\r")) {
            return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
    }
}

export type CsvColumn<TRow> = {
    key: keyof TRow & string;
    label: string;
};

export type Sheet = {
    name: string;
    data: SheetEntry[];
};

export interface SpreadsheetCell {
    getSpreadsheetCellValue(): string | number | boolean | undefined;
}
